import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DispatchRequestEntity, dispatchRequestStatus} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {IconThumbDown, IconThumbUp} from "@tabler/icons-react";

export type DispatchRequestDispatchAction = "COMMENT" | "ACCEPT" | "REJECT" | "NEW_DISPATCH"

export type DispatchRequestDispatchActionMenuDropdownProps = {
  dispatchRequest: DispatchRequestEntity
  onActionClick?: (action: DispatchRequestDispatchAction) => void
  showEmpty?: boolean
}

export const DispatchRequestDispatchActionMenuDropdown = (props: DispatchRequestDispatchActionMenuDropdownProps) => {
  const {
    dispatchRequest,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<DispatchRequestDispatchAction> = useMemo(() => {
    const actions = new Set<DispatchRequestDispatchAction>()

    if (hasAccess('dispatchrequest_edit')) {
      actions.add("COMMENT")
    }

    if (hasAccess('dispatch_edit')) {
      if (!dispatchRequest.reassignmentImminent) {
        switch (dispatchRequest.status) {
          case dispatchRequestStatus.pending:
            actions.add("ACCEPT")
            actions.add("REJECT")
            break
          case dispatchRequestStatus.accepted:
            actions.add("NEW_DISPATCH")
            break
          default:
            break
        }
      }
    }

    return actions
  }, [dispatchRequest, hasAccess])

  const onAddCommentClick = useCallback(() => {
    if (onActionClick) {
      onActionClick('COMMENT')
    }
  }, [onActionClick])

  const onAcceptClick = useCallback(() => {
    if (onActionClick) {
      onActionClick('ACCEPT')
    }
  }, [onActionClick])

  const onRejectClick = useCallback(() => {
    if (onActionClick) {
      onActionClick('REJECT')
    }
  }, [onActionClick])

  const onNewDispatchClick = useCallback(() => {
    if (onActionClick) {
      onActionClick('NEW_DISPATCH')
    }
  }, [onActionClick])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("COMMENT") &&
        <Menu.Item
          onClick={onAddCommentClick}>
          {t("common.action.comment.add", "Add Comment")}
        </Menu.Item>
      }
      {visibleActions.has("ACCEPT") &&
        <Menu.Item
          icon={<IconThumbUp size={14}/>}
          onClick={onAcceptClick}>
          {t("common.action.accept", "Accept")}
        </Menu.Item>
      }
      {visibleActions.has("REJECT") &&
        <Menu.Item
          color="red"
          icon={<IconThumbDown size={14}/>}
          onClick={onRejectClick}>
          {t("common.action.reject", "Reject")}
        </Menu.Item>
      }
      {visibleActions.has("NEW_DISPATCH") &&
        <Menu.Item
          onClick={onNewDispatchClick}>
          {t("common.action.dispatch", "New Dispatch")}
        </Menu.Item>
      }
    </>
  )
}
